// askSmart-ui/src/pages/MapleCoin/operations/MapleCoinOperations.tsx
//
// MapleCoin_Operations: the restricted institutional console — where customer
// money is, its state, and whether the internal ledger is consistent. Shown in
// the admin console only for the MapleCoin_Operations organization (by its
// configured org ID); every call is independently checked by the server.
//
// Read-only except the maker/checker steps (reversals; releasing holds and
// restrictions), placing a hold or restriction, and recording an integrity
// check. No journal creation, no edit, no delete.
import { useEffect, useState } from 'react';
import { Landmark } from 'lucide-react';
import DashboardTab from './DashboardTab';
import AccountsTab from './AccountsTab';
import TransactionsTab from './TransactionsTab';
import LedgerTab from './LedgerTab';
import AuditTab from './AuditTab';
import ExternalBankingTab from './ExternalBankingTab';
import ControlsTab from './ControlsTab';
import { onPeople } from './opsApi';
import { PeopleContext, Person } from './shared';
import './Operations.css';

export type OpsTab = 'dashboard' | 'accounts' | 'controls' | 'transactions' | 'ledger' | 'audit' | 'external';

/** Cross-tab navigation: open a transaction, a journal, or the approval queue. */
export interface OpsNav {
  openTransaction: (reference: string) => void;
  openJournal: (journalSeq: string) => void;
  openTransactions: (filter?: { accountNumber?: string; customerNumber?: string }) => void;
  openApprovals: () => void;
  /** Holds and restrictions: one account's, or (no argument) everything active. */
  openControls: (accountNumber?: string) => void;
}

const TABS: { key: OpsTab; label: string }[] = [
  { key: 'dashboard', label: 'Dashboard' },
  { key: 'accounts', label: 'Accounts' },
  { key: 'controls', label: 'Account controls' },
  { key: 'transactions', label: 'Transactions' },
  { key: 'ledger', label: 'Ledger' },
  { key: 'audit', label: 'Audit' },
  { key: 'external', label: 'External banking' },
];

type Focus = {
  transaction?: string;
  journal?: string;
  approvals?: boolean;
  controlsAccount?: string;
  filter?: { accountNumber?: string; customerNumber?: string };
  nonce: number;
};

export default function MapleCoinOperations() {
  const [tab, setTab] = useState<OpsTab>('dashboard');
  const [focus, setFocus] = useState<Focus>({ nonce: 0 });
  // Employee names for the ids shown anywhere in the console (display only).
  const [people, setPeople] = useState<Record<string, Person>>({});
  useEffect(() => onPeople((p) => setPeople((prev) => ({ ...prev, ...p }))), []);

  const go = (t: OpsTab, f: Omit<Focus, 'nonce'>) => {
    setFocus((prev) => ({ ...f, nonce: prev.nonce + 1 }));
    setTab(t);
  };
  const nav: OpsNav = {
    openTransaction: (reference) => go('transactions', { transaction: reference }),
    openJournal: (journalSeq) => go('ledger', { journal: journalSeq }),
    openTransactions: (filter) => go('transactions', { filter }),
    openApprovals: () => go('transactions', { approvals: true }),
    openControls: (accountNumber) => go('controls', { controlsAccount: accountNumber }),
  };

  return (
    <PeopleContext.Provider value={people}>
    <div className="mco">
      <header className="mco-head">
        <div className="mco-title">
          <span className="mco-mark"><Landmark size={18} /></span>
          <div>
            <h1>MapleCoin Operations</h1>
            <p>Institutional financial operations</p>
          </div>
        </div>
        <nav className="mco-tabs" aria-label="MapleCoin Operations">
          {TABS.map((t) => (
            <button key={t.key} type="button" role="tab" aria-selected={tab === t.key}
              className={`mco-tab${tab === t.key ? ' mco-tab--on' : ''}`}
              onClick={() => go(t.key, {})}>
              {t.label}
            </button>
          ))}
        </nav>
      </header>
      <main className="mco-body">
        {tab === 'dashboard' && <DashboardTab nav={nav} />}
        {tab === 'accounts' && <AccountsTab nav={nav} />}
        {tab === 'controls' && <ControlsTab key={focus.nonce} nav={nav} initialAccount={focus.controlsAccount} />}
        {tab === 'transactions' && (
          <TransactionsTab key={focus.nonce} nav={nav} initialReference={focus.transaction}
            initialApprovals={focus.approvals} initialFilter={focus.filter} />
        )}
        {tab === 'ledger' && <LedgerTab key={focus.nonce} nav={nav} initialJournal={focus.journal} />}
        {tab === 'audit' && <AuditTab nav={nav} />}
        {tab === 'external' && <ExternalBankingTab />}
      </main>
    </div>
    </PeopleContext.Provider>
  );
}
